// frontend/src/components/placements/FollowupStagesPage.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const FollowupStagesPage = () => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [formData, setFormData] = useState({
    stage_name: "",
    stage_order: "",
    days_after_previous: "",
    description: "",
    is_required: false,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadStages = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const res = await secureAxios.get(`${apiBase}/api/followup-stages`, {
        params: { tenant_id: user.tenant_id },
      });
      setStages(res.data);
    } catch (err) {
      console.error("Error loading stages:", err);
      toast.error("Failed to load follow-up stages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStages();
  }, [user?.tenant_id]);

  const resetForm = () => {
    setFormData({
      stage_name: "",
      stage_order: stages.length + 1,
      days_after_previous: "",
      description: "",
      is_required: false,
      is_active: true,
    });
    setEditingStage(null);
    setError("");
  };

  const handleCreate = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      stage_order: stages.length + 1,
    }));
    setShowForm(true);
  };

  const handleEdit = (stage) => {
    setEditingStage(stage);
    setFormData({
      stage_name: stage.stage_name || "",
      stage_order: stage.stage_order || "",
      days_after_previous: stage.days_after_previous || "",
      description: stage.description || "",
      is_required: stage.is_required || false,
      is_active: stage.is_active !== false,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.stage_name.trim()) {
      setError("Stage name is required");
      return;
    }

    if (!formData.stage_order) {
      setError("Stage order is required");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      ...formData,
      tenant_id: user.tenant_id,
      stage_order: parseInt(formData.stage_order),
      days_after_previous: formData.days_after_previous
        ? parseInt(formData.days_after_previous)
        : null,
      [editingStage ? "updated_by" : "created_by"]: user.user_id,
    };

    try {
      if (editingStage) {
        await secureAxios.put(
          `${apiBase}/api/followup-stages/${editingStage.stage_id}`,
          payload
        );
        toast.success("Stage updated successfully");
      } else {
        await secureAxios.post(`${apiBase}/api/followup-stages`, payload);
        toast.success("Stage created successfully");
      }

      setShowForm(false);
      resetForm();
      loadStages();
    } catch (err) {
      console.error("Error saving stage:", err);
      setError(err.response?.data?.error || "Failed to save stage");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (stage) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the "${stage.stage_name}" stage?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/followup-stages/${stage.stage_id}`,
        { params: { tenant_id: user.tenant_id } }
      );
      toast.success("Stage deleted successfully");
      loadStages();
    } catch (err) {
      console.error("Error deleting stage:", err);
      toast.error(err.response?.data?.error || "Failed to delete stage");
    }
  };

  const handleReorder = async (stageId, direction) => {
    const currentIndex = stages.findIndex((s) => s.stage_id === stageId);
    if (
      (direction === "up" && currentIndex === 0) ||
      (direction === "down" && currentIndex === stages.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const reorderedStages = [...stages];
    const [movedStage] = reorderedStages.splice(currentIndex, 1);
    reorderedStages.splice(newIndex, 0, movedStage);

    // Update order in database
    try {
      await Promise.all(
        reorderedStages.map((stage, index) =>
          secureAxios.put(`${apiBase}/api/followup-stages/${stage.stage_id}`, {
            ...stage,
            stage_order: index + 1,
            tenant_id: user.tenant_id,
            updated_by: user.user_id,
          })
        )
      );
      loadStages();
    } catch (err) {
      console.error("Error reordering stages:", err);
      toast.error("Failed to reorder stages");
    }
  };

  return (
    <div className="container-fluid py-3 py-md-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 gap-2">
        <h4 className="mb-0 h5 h4-md">
          <i className="bi bi-list-check me-2"></i>
          Follow-up Stages
        </h4>
        {!showForm && (
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="bi bi-plus-circle me-2"></i>
            <span className="d-none d-sm-inline">Add </span>Stage
          </button>
        )}
      </div>

      {/* Info Card */}
      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <div className="d-flex align-items-start">
          <i className="bi bi-info-circle me-2 mt-1 flex-shrink-0"></i>
          <div>
            <strong>Configure Outreach Workflow:</strong>
            <span className="d-none d-sm-inline">
              {" "}Define the stages your team follows when reaching out to
              employers for job placements. These stages help track progress and
              ensure consistent follow-up processes.
            </span>
            <span className="d-sm-none">
              {" "}Define stages for employer outreach tracking.
            </span>
          </div>
        </div>
      </div>

      {showForm ? (
        <div className="card">
          <div className="card-header bg-primary text-white py-2">
            <i className="bi bi-pencil me-2"></i>
            {editingStage ? "Edit Stage" : "Add New Stage"}
          </div>
          <div className="card-body p-2 p-md-3">
            {error && (
              <div className="alert alert-danger alert-dismissible">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                ></button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-2 g-md-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">
                    Stage Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="stage_name"
                    className="form-control form-control-sm"
                    value={formData.stage_name}
                    onChange={handleChange}
                    placeholder="e.g., Initial Call, Walk-in Visit"
                    required
                    disabled={saving}
                  />
                </div>

                <div className="col-6 col-md-3">
                  <label className="form-label">
                    Order <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="stage_order"
                    className="form-control form-control-sm"
                    value={formData.stage_order}
                    onChange={handleChange}
                    min="1"
                    required
                    disabled={saving}
                  />
                </div>

                <div className="col-6 col-md-3">
                  <label className="form-label">Days After</label>
                  <input
                    type="number"
                    name="days_after_previous"
                    className="form-control form-control-sm"
                    value={formData.days_after_previous}
                    onChange={handleChange}
                    min="0"
                    placeholder="Optional"
                    disabled={saving}
                  />
                  <small className="text-muted d-none d-sm-block">
                    Suggested timing
                  </small>
                </div>

                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control form-control-sm"
                    value={formData.description}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Instructions or notes for this stage..."
                    disabled={saving}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="is_required"
                      className="form-check-input"
                      id="is_required"
                      checked={formData.is_required}
                      onChange={handleChange}
                      disabled={saving}
                    />
                    <label className="form-check-label" htmlFor="is_required">
                      Required Stage
                    </label>
                    <small className="text-muted d-block">
                      Must be completed before moving forward
                    </small>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="is_active"
                      className="form-check-input"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={saving}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active
                    </label>
                    <small className="text-muted d-block">
                      Inactive stages won't appear in selection
                    </small>
                  </div>
                </div>
              </div>

              <div className="mt-4 d-flex flex-column flex-sm-row gap-2">
                <button
                  type="submit"
                  className="btn btn-primary order-1"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      {editingStage ? "Update Stage" : "Create Stage"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary order-2"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : stages.length === 0 ? (
        <div className="text-center py-5">
          <i
            className="bi bi-list-check text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Follow-up Stages Defined</h5>
          <p className="text-muted">
            Create your first stage to define your outreach workflow
          </p>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="bi bi-plus-circle me-2"></i>
            Add First Stage
          </button>
        </div>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div className="card d-none d-md-block">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "80px" }}>Order</th>
                      <th>Stage Name</th>
                      <th>Days After</th>
                      <th>Description</th>
                      <th>Required</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((stage, index) => (
                      <tr
                        key={stage.stage_id}
                        className={!stage.is_active ? "table-secondary" : ""}
                      >
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => handleReorder(stage.stage_id, "up")}
                              disabled={index === 0}
                              title="Move Up"
                              aria-label="Move stage up"
                            >
                              <i className="bi bi-chevron-up"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() =>
                                handleReorder(stage.stage_id, "down")
                              }
                              disabled={index === stages.length - 1}
                              title="Move Down"
                              aria-label="Move stage down"
                            >
                              <i className="bi bi-chevron-down"></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <strong>{stage.stage_name}</strong>
                        </td>
                        <td>
                          {stage.days_after_previous
                            ? `${stage.days_after_previous} days`
                            : "-"}
                        </td>
                        <td>
                          <small className="text-muted">
                            {stage.description || "-"}
                          </small>
                        </td>
                        <td>
                          {stage.is_required ? (
                            <span className="badge bg-warning text-dark">
                              Required
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark">
                              Optional
                            </span>
                          )}
                        </td>
                        <td>
                          {stage.is_active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Inactive</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEdit(stage)}
                              title="Edit"
                              aria-label={`Edit ${stage.stage_name}`}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDelete(stage)}
                              title="Delete"
                              aria-label={`Delete ${stage.stage_name}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-md-none">
            {stages.map((stage, index) => (
              <div
                key={stage.stage_id}
                className={`card mb-3 ${!stage.is_active ? "bg-light" : ""}`}
              >
                <div className="card-body p-2 p-md-3">
                  {/* Header with name and status badges */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0">
                      <span className="badge bg-secondary me-2">
                        #{stage.stage_order}
                      </span>
                      {stage.stage_name}
                    </h6>
                    <div className="d-flex gap-1">
                      {stage.is_required && (
                        <span className="badge bg-warning text-dark">Req</span>
                      )}
                      {stage.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  {stage.days_after_previous && (
                    <div className="mb-2 small">
                      <i className="bi bi-clock me-1 text-muted"></i>
                      {stage.days_after_previous} days after previous
                    </div>
                  )}

                  {stage.description && (
                    <p className="text-muted small mb-2">{stage.description}</p>
                  )}

                  {/* Actions */}
                  <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                    {/* Reorder buttons */}
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handleReorder(stage.stage_id, "up")}
                        disabled={index === 0}
                        aria-label="Move stage up"
                      >
                        <i className="bi bi-chevron-up"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handleReorder(stage.stage_id, "down")}
                        disabled={index === stages.length - 1}
                        aria-label="Move stage down"
                      >
                        <i className="bi bi-chevron-down"></i>
                      </button>
                    </div>

                    {/* Edit/Delete buttons */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(stage)}
                        aria-label={`Edit ${stage.stage_name}`}
                      >
                        <i className="bi bi-pencil me-1"></i> Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(stage)}
                        aria-label={`Delete ${stage.stage_name}`}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!showForm && stages.length > 0 && (
        <div className="text-muted small mt-2">
          {stages.length} stage{stages.length !== 1 ? "s" : ""} configured
        </div>
      )}
    </div>
  );
};

export default FollowupStagesPage;